import { calculateNextExecutionTime } from './scheduledJobsHelper.js'

export const MIGRATION_FORMAT = 'hanjiang-helper-migration'
export const MIGRATION_VERSION = 2
export const APP_STORAGE_KEY = 'hanjiang-helper-storage'
export const MAX_BACKUP_BYTES = 20 * 1024 * 1024

const dangerousKeys = new Set(['__proto__', 'prototype', 'constructor'])
const privateKey = /^(password|token|token_?uid|cookie|signature|userInfo|loginLocks|proxyAccessKey|accessKey|proxyKey|authorization)$/i
const isObject = value => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const APP_CONFIG_FIELDS = ['referrer', 'hjsgServer', 'activitySkipEnabled', 'dailyRuntimeResetEnabled']
const appConfigOnly = config => Object.fromEntries(APP_CONFIG_FIELDS
  .filter(key => Object.hasOwn(config, key)).map(key => [key, config[key]]))

function checkTree(value, depth = 0) {
  if (depth > 40) throw new Error('备份数据嵌套过深')
  if (!value || typeof value !== 'object') return
  for (const [key, child] of Object.entries(value)) {
    if (dangerousKeys.has(key)) throw new Error('备份包含不安全的字段')
    checkTree(child, depth + 1)
  }
}

function clean(value) {
  if (Array.isArray(value)) return value.map(clean)
  if (!isObject(value)) return value
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !privateKey.test(key) && !dangerousKeys.has(key))
    .map(([key, child]) => [key, clean(child)]))
}

function object(value, label) {
  if (!isObject(value)) throw new Error(`${label}格式不正确`)
  return value
}

function strings(value, label) {
  if (!Array.isArray(value) || !value.every(item => typeof item === 'string')) {
    throw new Error(`${label}必须是字符串列表`)
  }
  return [...new Set(value)]
}

function learningData(source, accounts) {
  const result = {}
  const bank = source.virtuous_pop___global__?.answerBank
  if (bank) result.virtuous_pop___global__ = { answerBank: clean(bank) }
  for (const accountKey of Object.keys(accounts)) {
    for (const [task, field] of [['black_house', 'targetLearning'], ['resource_harvest', 'plunderLearning']]) {
      const value = source[`${task}_${accountKey}`]?.[field]
      if (value) result[`${task}_${accountKey}`] = { [field]: clean(value) }
    }
  }
  return result
}

function scheduledPlans(source, accounts) {
  object(source, '定时计划')
  return Object.fromEntries(Object.entries(source).map(([id, job]) => {
    object(job, '定时计划')
    if (!id || job.id !== id) throw new Error('定时计划标识不一致')
    const tasks = strings(job.tasks, '定时任务选择')
    const selectedAccounts = strings(job.accounts, '定时计划账号')
    if (!tasks.length || !selectedAccounts.length || selectedAccounts.some(key => !Object.hasOwn(accounts, key))) {
      throw new Error('定时计划任务为空或引用了不存在的账号')
    }
    object(job.configs, '定时计划参数')
    for (const config of Object.values(job.configs)) object(config, '定时任务参数')
    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(job.originalScheduleTime)) throw new Error('定时计划执行时间不正确')
    for (const field of ['scheduledTime', 'createdAt']) {
      if (typeof job[field] !== 'string' || !Number.isFinite(Date.parse(job[field]))) throw new Error('定时计划日期不正确')
    }
    if (!['scheduled', 'executing', 'expired', 'failed'].includes(job.status)) throw new Error('定时计划状态不正确')
    if (typeof job.requestInterval !== 'number' || !Number.isFinite(job.requestInterval) || job.requestInterval < 0) {
      throw new Error('定时计划请求间隔不正确')
    }
    return [id, { id, tasks, accounts: selectedAccounts, configs: clean(job.configs),
      requestInterval: job.requestInterval, originalScheduleTime: job.originalScheduleTime,
      scheduledTime: job.scheduledTime, createdAt: job.createdAt, status: job.status }]
  }))
}

export function buildMigrationBackup(state, { includePasswords = false, now = new Date() } = {}) {
  const accounts = Object.fromEntries(Object.entries(state.accounts || {}).map(([key, account]) => [key, {
    username: account.username,
    server: account.server,
    ...(includePasswords ? { password: account.password || '' } : {}),
  }]))
  const userGroups = clean(state.userGroups || {})
  // 删除账号后旧用户组可能仍引用它；导出时只保留实际存在的账号。
  for (const group of Object.values(userGroups)) {
    group.selectedAccounts = (group.selectedAccounts || []).filter(key => Object.hasOwn(accounts, key))
  }
  const snapshot = {
    format: MIGRATION_FORMAT,
    version: MIGRATION_VERSION,
    exportedAt: now.toISOString(),
    includesPasswords: includePasswords,
    data: {
      accounts,
      activeAccount: Object.hasOwn(accounts, state.activeAccount) ? state.activeAccount : null,
      config: appConfigOnly(state.config || {}),
      globalTaskConfigs: clean(state.globalTaskConfigs || {}),
      taskGroups: clean(state.taskGroups || {}),
      userGroups,
      taskSelector: clean(state.taskSelector || { selectedCategory: 'all' }),
      selectedTaskIds: [...(state.selectedTaskIdsByRuntime?.browser || state.selectedTaskIds || [])],
      scheduledJobs: scheduledPlans(state.scheduledJobs || {}, accounts),
      taskExecutionData: learningData(state.taskExecutionData || {}, accounts),
    },
  }
  // 使用与恢复一致的校验，避免生成无法恢复的文件。
  return validateMigrationBackup(snapshot)
}

function validateMigrationBackup(snapshot) {
  checkTree(snapshot)
  if (snapshot.format !== MIGRATION_FORMAT) throw new Error('请选择迁移备份文件，而非账号或批量配置文件')
  if (![1, MIGRATION_VERSION].includes(snapshot.version)) throw new Error('不支持此迁移备份版本')
  if (typeof snapshot.includesPasswords !== 'boolean') throw new Error('备份缺少密码选项标识')
  if (typeof snapshot.exportedAt !== 'string' || !Number.isFinite(Date.parse(snapshot.exportedAt))) {
    throw new Error('备份导出时间不正确')
  }
  const data = object(snapshot.data, '备份数据')
  object(data.accounts, '账号')
  for (const [key, account] of Object.entries(data.accounts)) {
    object(account, '账号')
    if (typeof account.username !== 'string' || !account.username.trim() ||
        typeof account.server !== 'string' || !account.server.trim() ||
        key !== `${account.username}_S${account.server}`) throw new Error('账号标识与用户名、服务器不一致')
    if (account.password !== undefined && (typeof account.password !== 'string' || !snapshot.includesPasswords)) {
      throw new Error('账号密码与备份选项不一致')
    }
  }
  if (data.activeAccount !== null && !Object.hasOwn(data.accounts, data.activeAccount)) {
    throw new Error('当前账号引用不存在')
  }
  for (const field of ['config', 'globalTaskConfigs', 'taskGroups', 'userGroups', 'taskSelector', 'taskExecutionData']) {
    object(data[field], field)
  }
  for (const field of ['activitySkipEnabled', 'dailyRuntimeResetEnabled']) {
    if (data.config[field] !== undefined && typeof data.config[field] !== 'boolean') throw new Error('应用开关格式不正确')
  }
  for (const field of ['referrer', 'hjsgServer']) {
    if (data.config[field] !== undefined && typeof data.config[field] !== 'string') throw new Error('应用配置格式不正确')
  }
  if (data.taskSelector.selectedCategory !== undefined && typeof data.taskSelector.selectedCategory !== 'string') {
    throw new Error('任务筛选格式不正确')
  }
  for (const config of Object.values(data.globalTaskConfigs)) object(config, '任务配置')
  strings(data.selectedTaskIds, '任务选择')
  for (const group of Object.values(data.taskGroups)) {
    object(group, '任务组')
    if (typeof group.name !== 'string') throw new Error('任务组名称格式不正确')
    strings(group.selectedTasks, '任务组选择')
    object(group.taskConfigs || {}, '任务组参数')
    for (const config of Object.values(group.taskConfigs || {})) object(config, '任务组参数')
  }
  for (const group of Object.values(data.userGroups)) {
    object(group, '用户组')
    if (typeof group.name !== 'string') throw new Error('用户组名称格式不正确')
    for (const key of strings(group.selectedAccounts, '用户组账号')) {
      if (!Object.hasOwn(data.accounts, key)) throw new Error('用户组引用了不存在的账号')
    }
  }
  const allowed = learningData(data.taskExecutionData, data.accounts)
  for (const key of Object.keys(data.taskExecutionData)) {
    if (!Object.hasOwn(allowed, key)) throw new Error('备份包含非长期学习数据或无效账号引用')
  }
  const bank = allowed.virtuous_pop___global__?.answerBank
  if (bank) {
    object(bank, '题库')
    for (const entry of Object.values(bank)) {
      object(entry, '题库条目')
      if (typeof entry.correctText !== 'string' || !['a', 'b', 'c', 'd'].includes(entry.correctOption)) {
        throw new Error('题库答案格式不正确')
      }
    }
  }
  for (const [key, entry] of Object.entries(allowed)) {
    if (key === 'virtuous_pop___global__') continue
    const learning = object(entry.targetLearning || entry.plunderLearning, '目标学习数据')
    for (const field of ['successfulTargets', 'blacklist']) {
      for (const target of Object.values(object(learning[field], '目标列表'))) {
        object(target, '学习目标')
        if (typeof target.key !== 'string' || typeof target.uid !== 'string') throw new Error('学习目标标识不正确')
      }
    }
  }
  const accounts = Object.fromEntries(Object.entries(data.accounts).map(([key, account]) => [key, {
    username: account.username, server: account.server,
    ...(snapshot.includesPasswords ? { password: account.password || '' } : {}),
  }]))
  return {
    format: MIGRATION_FORMAT, version: MIGRATION_VERSION,
    exportedAt: snapshot.exportedAt, includesPasswords: snapshot.includesPasswords,
    data: {
      accounts, activeAccount: data.activeAccount,
      config: appConfigOnly(data.config), globalTaskConfigs: clean(data.globalTaskConfigs),
      taskGroups: clean(data.taskGroups), userGroups: clean(data.userGroups),
      taskSelector: clean(data.taskSelector), selectedTaskIds: strings(data.selectedTaskIds, '任务选择'),
      scheduledJobs: snapshot.version === 1 ? {} : scheduledPlans(data.scheduledJobs, accounts),
      taskExecutionData: allowed,
    },
  }
}

export function parseMigrationBackup(text) {
  if (new TextEncoder().encode(text).length > MAX_BACKUP_BYTES) throw new Error('备份文件超过 20 MB')
  let snapshot
  try { snapshot = JSON.parse(text) } catch { throw new Error('文件不是有效的 JSON') }
  return validateMigrationBackup(object(snapshot, '备份'))
}

export function summarizeMigrationBackup(snapshot) {
  let successfulTargets = 0
  let blacklistedTargets = 0
  for (const entry of Object.values(snapshot.data.taskExecutionData)) {
    const learning = entry.targetLearning || entry.plunderLearning
    if (learning) {
      successfulTargets += Object.keys(learning.successfulTargets).length
      blacklistedTargets += Object.keys(learning.blacklist).length
    }
  }
  return {
    accounts: Object.keys(snapshot.data.accounts).length,
    answers: Object.keys(snapshot.data.taskExecutionData.virtuous_pop___global__?.answerBank || {}).length,
    successfulTargets, blacklistedTargets,
    scheduledJobs: Object.keys(snapshot.data.scheduledJobs || {}).length,
  }
}

export function migrationStatePatch(snapshot, now = new Date()) {
  const { accounts, config, ...rest } = snapshot.data
  // 部署环境相关参数不从旧站迁移；仅迁移现有应用的用户选项。
  const appConfig = appConfigOnly(config)
  return {
    ...rest,
    accounts: Object.fromEntries(Object.entries(accounts).map(([key, account]) => [key, {
      ...account, password: account.password || '', token: null, tokenUid: null, userInfo: null,
    }])),
    config: appConfig,
    scheduledJobs: Object.fromEntries(Object.entries(rest.scheduledJobs || {}).map(([id, job]) => [id,
      ['scheduled', 'executing'].includes(job.status) ? { ...job, status: 'scheduled',
        scheduledTime: calculateNextExecutionTime(job.originalScheduleTime, now).toISOString() } : job,
    ])),
    loginLocks: {}, lastDailyRuntimeResetDate: null,
    selectedTaskIdsByRuntime: { browser: [...rest.selectedTaskIds], backend: [] },
  }
}

// Zustand persist 在落盘失败前可能已更新内存，因此同时回滚内存与原始存储。
export function commitMigrationRestore({ snapshot, getState, setState, storage }) {
  snapshot = validateMigrationBackup(snapshot)
  const previous = getState()
  const raw = storage.getItem(APP_STORAGE_KEY)
  const patch = migrationStatePatch(snapshot)
  const next = { ...patch, config: { ...previous.config, ...patch.config } }
  try {
    setState(next)
  } catch {
    try { setState(previous, true) } catch { /* setState 已恢复内存，持久化可能仍被浏览器拒绝。 */ }
    try {
      if (raw === null) storage.removeItem(APP_STORAGE_KEY)
      else storage.setItem(APP_STORAGE_KEY, raw)
    } catch { /* 配额/存储权限错误时保留已有存储，不继续恢复。 */ }
    throw new Error('恢复未完成：本地存储写入失败，原数据已保留；运行任务已停止')
  }
}
